import { useState } from 'react';
import { useMembers, useInviteMember, useUpdateMemberRole, useRemoveMember } from '../hooks/useApi';
import type { TeamRole } from '../types';
import { ConfirmModal } from '../components/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';

const ROLES: TeamRole[] = ['admin', 'editor', 'viewer'];

export function TeamPage() {
  const { t } = useTranslation('settings');
  const formatDate = useFormatDate();
  const { data, isLoading } = useMembers();
  const { mutate: invite, isPending: inviting } = useInviteMember();
  const { mutate: updateRole } = useUpdateMemberRole();
  const { mutate: removeMember } = useRemoveMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('editor');
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const myRole = data?.role ?? 'owner';
  const canManage = myRole === 'owner' || myRole === 'admin';
  const members = data?.members ?? [];

  function handleInvite() {
    setError(null);
    invite({ email: email.trim(), role }, {
      onSuccess: () => setEmail(''),
      onError: (e: unknown) => setError(e instanceof Error ? e.message : t('team.inviteError')),
    });
  }

  const roleLabel = (r: string) => t(`team.roles.${r}`, r);

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-narrow">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('team.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('team.subtitle')}</p>
        </div>

        {/* Invite form — managers only */}
        {canManage && (
          <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <label className="block text-xs font-medium text-gray-700 mb-2">{t('team.inviteLabel')}</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('team.emailPlaceholder')}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TeamRole)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              >
                {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
              <button
                onClick={handleInvite}
                disabled={inviting || !email.trim()}
                className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {inviting ? t('team.inviting') : t('team.invite')}
              </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          </div>
        )}

        {/* Members list */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-2xl border border-gray-200 bg-white animate-pulse" />)}
          </div>
        ) : members.length > 0 ? (
          <div className="space-y-3">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-card">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">{m.email}</span>
                    {m.status === 'pending' && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{t('team.pending')}</span>
                    )}
                  </div>
                  {m.role !== 'owner' && (
                    <span className="text-xs text-gray-400">
                      {m.status === 'pending'
                        ? t('team.invitedOn', { date: formatDate(m.invitedAt) })
                        : t('team.joinedOn', { date: formatDate(m.acceptedAt ?? m.invitedAt) })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {canManage && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={(e) => updateRole({ memberId: m.id, role: e.target.value as TeamRole })}
                      className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-primary-400 focus:outline-none"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                    </select>
                  ) : (
                    <span className="text-xs font-medium text-gray-500">{roleLabel(m.role)}</span>
                  )}
                  {canManage && m.role !== 'owner' && (
                    <button
                      onClick={() => setConfirmAction({
                        title: t('team.removeTitle'),
                        message: t('team.removeMessage', { email: m.email }),
                        onConfirm: () => { removeMember(m.id); setConfirmAction(null); },
                      })}
                      className="rounded-lg border border-red-100 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors"
                    >
                      {t('team.remove')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <p className="text-sm text-gray-400">{t('team.noMembers')}</p>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('team.confirmRemove')}
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
